import AgoraRTC from 'agora-rtc-sdk-ng'

import Agora from './agora'
import Trtc from './trtc'
import Zego from './zego'

export type ActMediaType = 'mainVideo' | 'mainScreen' | 'viceVideo' | 'viceScreen' | 'audio' | 'all'

// 不需翻转 NORMAL = 1;
// 上下镜像 REVERSAL = 2;
// 左右镜像 FLIP = 3;
// 左右镜像+上下镜像 FLIPREVERSAL = 4;
export type Correction = 1 | 2 | 3 | 4

export const CorrectStyleName = ['', '默认', '上下镜像', '左右镜像', '左右镜像+上下镜像']
export const CorrectStyle = ['', '', 'correct_flip', 'correct_mirror', 'correct_flip_mirror']

// 1: 还原上次透视矫正
// 2：保存这次透视矫正
// 3：还原视频流到初始状态
export type Correct = 1 | 2 | 3

export type CorrectType = 'main' | 'vice'

export type MediaType = 'video' | 'audio'

export type RtcLine = 'zego' | 'agora' | 'trtc'

export type UseDeviceName = 'mainCamera' | 'viceCamera' | 'microphone' | 'playback'

export interface PlayConf {
  fit?: 'cover' | 'contain' | 'fill'
  mirror?: boolean
  type?: 'local' | 'remote'
}

export interface RemoteUser {
  uid: number
  videoTrack?: any
  audioTrack?: any
}

export interface SettingDevices {
  key: UseDeviceName
  title: string
  subTitle: string
  devices: MediaDeviceInfo[]
}

export interface RtcOptions {
  channel: string
  uid: number
  token?: string
  viceToken?: string
}

type ScreenBounds = {
  x: number
  y: number
  width: number
  height: number
}

export interface ChangeCamera {
  deviceId: string
  microphoneId?: string
  publish?: boolean
  canvas?: boolean
  screen?: { id?: string; bounds?: ScreenBounds; workArea?: ScreenBounds } | null
  status?: Correct
}

export interface ChangeMicrophone {
  deviceId: string
  publish?: boolean
}

export default class Rtc {
  public client: Agora | Zego | Trtc | null = null
  public line: RtcLine | undefined

  get remoteVideoTrack(): any[] {
    return this.client!.remoteVideoTrack
  }

  get localMainVideoTrack() {
    return this.client!.localMainVideoTrack
  }

  get localViceVideoTrack() {
    return this.client!.localViceVideoTrack
  }

  // 初始化实例
  public async ready(line: RtcLine) {
    this.line = line || 'agora'
    this.client = null
    switch (this.line) {
      case 'agora':
        this.client = new Agora(import.meta.env.VITE_APP_AGORA_APPID)
        break
      case 'zego':
        this.client = new Zego(import.meta.env.VITE_APP_ZEGO_APPID, import.meta.env.VITE_APP_ZEGO_URL || '')
        break
      case 'trtc':
        this.client = new Trtc(import.meta.env.VITE_APP_TRTC_APPID)
        break
    }
  }

  // 登录初始化
  public init(options: RtcOptions) {
    return this.client!.init(options)
  }

  // 推流
  public publish(type: ActMediaType = 'all') {
    return this.client!.publish(type)
  }

  // play
  public play(track: any, element: HTMLElement, config?: PlayConf, uid?: number) {
    return this.client!.play(track, element, config, uid)
  }

  // 销毁
  public async destroy() {
    await this.client!.destroy()
    this.client = null
  }

  /**
   * 设置镜像信息
   * @param type 主副
   * @param status 矫正类型
   */
  public setCorrect(type: CorrectType, status: Correct) {
    if (this.line === 'agora') {
      ;(this.client as Agora).setCorrect(type, status)
    }
  }

  public async changeMainCamera({ deviceId, microphoneId = '', publish = true, canvas = false, status = 1 }: ChangeCamera) {
    await this.client!.changeMainCamera({ deviceId, microphoneId, publish, canvas, status })
    // 这里除了即构线路，走独立切换麦克风，changeMainCamera不存在将麦克风关闭的情况，不考虑microphoneId的场景，关闭麦克风需要走changeMicrophone
    if (this.line !== 'zego' && microphoneId) {
      await this.client!.changeMicrophone({ deviceId: microphoneId, publish })
    }
  }

  public changeViceCamera({ deviceId, publish = true, canvas = false, status = 1 }: ChangeCamera) {
    return this.client!.changeViceCamera({ deviceId, publish, canvas, status })
  }

  public changeMicrophone({ deviceId, publish = true }: ChangeMicrophone) {
    return this.client!.changeMicrophone({ deviceId, publish })
  }

  public deviceOn(type: 'mainVideo' | 'viceVideo' | 'audio') {
    return this.client!.deviceOn(type)
  }

  public deviceOff(type: 'mainVideo' | 'viceVideo' | 'audio') {
    return this.client!.deviceOff(type)
  }

  on(eventName: string | symbol, listener: (...args: any[]) => void) {
    console.log('rtc on', eventName)
    return this.client!.on(eventName, listener)
  }

  removeListener(eventName: string | symbol, listener: (...args: any[]) => void) {
    return this.client!.removeListener(eventName, listener)
  }

  public static getCameras(): Promise<MediaDeviceInfo[]> {
    return Trtc.getCameras()
  }

  public static getMicrophones(): Promise<MediaDeviceInfo[]> {
    return Trtc.getMicrophones()
  }

  public static getPlaybacks(): Promise<MediaDeviceInfo[]> {
    return Trtc.getPlaybacks()
  }

  public static async getSettingDevices(): Promise<SettingDevices[]> {
    return Trtc.getSettingDevices()
  }
}
